/**
 * growth-series — the hero chart's display-series resolver (Paul's day-one
 * value-line ruling, 2026-07-15).
 *
 * The 12-month "Total trust value" chart is driven by AUM snapshots that
 * materialise ONE PER MONTH (nightly aggregator), so a young account has 0-1
 * points and used to get "no chart yet" placeholder text + empty space —
 * exactly the day-one bareness Paul flagged: "if I log into my Binance
 * account on day one there IS a line… technically before they filled it in
 * it was zero, now it's where it is."
 *
 * This module decides, PURELY, what the chart should draw:
 *   - ≥2 real snapshots        → the real series, untouched (real always wins);
 *   - <2 points but a real
 *     current value            → a SYNTHETIC two-point series from R0 at the
 *                                start of the selected range rising to the
 *                                current value at this month, drawn with the
 *                                same line/area styling as the real chart,
 *                                plus an honest "First valuation · Jul 2026"
 *                                annotation on today's point and extra
 *                                y-headroom so the value lands comfortably in
 *                                the upper-middle of the plot (not kissing
 *                                the top edge);
 *   - no points and no value   → empty (the hero keeps its honest
 *                                "appears as valuations update" placeholder).
 *
 * HONESTY NOTE: the synthetic line invents no interior data — it renders
 * exactly two facts: the on-platform value was R0 before the account existed,
 * and it is R<current> now. The annotation names the single real valuation
 * date so a reader can't mistake the ramp for 12 months of history.
 */

import type { GrowthPoint } from '../charts/growth-chart';

/** Extra y-domain headroom for the synthetic day-one line: the current value
 *  sits ~72% up the plot instead of ~92% (the real-series default), reading
 *  as a deliberate composition rather than a line pinned to the ceiling. */
export const SYNTHETIC_HEADROOM = 0.45;

/** Months of history each range toggle spans — mirrors the real chart's
 *  windows (1M/3M/6M trail 1/3/6 months; 1Y the full 12). */
export function monthsBackForRange(range: string): number {
  switch (range) {
    case '1M':
      return 1;
    case '3M':
      return 3;
    case '6M':
      return 6;
    default:
      return 12;
  }
}

/** 'YYYY-MM' for a date shifted back N months — same label format the real
 *  AUM snapshots carry (`AumSnapshotView.month`), so the x-axis reads
 *  identically in both modes. */
function isoMonth(base: Date, monthsBack: number): string {
  const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() - monthsBack, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** "Jul 2026" — the annotation's human date. */
function shortMonthYear(d: Date): string {
  return d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The quiet, honest annotation on the synthetic line's endpoint. */
export function dayOneAnnotationLabel(asOf: Date): string {
  return `First valuation · ${shortMonthYear(asOf)}`;
}

export interface GrowthDisplayInput {
  /** The REAL chronological snapshot series (from `snapshotsToGrowthSeries`). */
  realSeries: GrowthPoint[];
  /** The selected range toggle ('1M' | '3M' | '6M' | '1Y'). */
  range: string;
  /** The hero's headline value (whole Rand) — what the synthetic line rises
   *  to when there are no snapshots yet. */
  currentValueZar: number;
  /** When the current value was struck (a lone snapshot's month, or the
   *  summary's lastUpdated). Used for the annotation date. Defaults to now. */
  asOf?: Date | null;
  /** Injectable clock for tests. */
  now?: Date;
}

export type GrowthDisplay =
  | { kind: 'real'; points: GrowthPoint[] }
  | {
      kind: 'synthetic';
      points: GrowthPoint[];
      annotation: { month: string; label: string };
      headroom: number;
    }
  | { kind: 'empty' };

/**
 * Decide what the hero chart draws. Pure — trivially unit-testable, and the
 * takeover rule ("as soon as ≥2 real snapshots exist, the real series wins,
 * untouched") lives in exactly one place.
 */
export function resolveGrowthDisplay(input: GrowthDisplayInput): GrowthDisplay {
  const { realSeries, range } = input;

  // Real history takes over the moment it exists — untouched.
  if (realSeries.length >= 2) return { kind: 'real', points: realSeries };

  // A lone real snapshot IS the current value (it's the same figure the
  // nightly aggregator materialised); otherwise use the hero headline.
  const lone = realSeries.length === 1 ? realSeries[0]! : null;
  const currentValue = lone && lone.value > 0 ? lone.value : input.currentValueZar;

  // Nothing valued yet → no line to draw; the hero keeps its honest
  // empty-state copy. An R0→R0 flat line would be worse than saying so.
  if (!(currentValue > 0)) return { kind: 'empty' };

  const now = input.now ?? new Date();
  const currentMonth = isoMonth(now, 0);
  const startMonth = isoMonth(now, monthsBackForRange(range));

  const points: GrowthPoint[] = [
    // "Technically before they filled it in it was zero" — Paul, verbatim.
    { month: startMonth, value: 0, q: false },
    { month: currentMonth, value: currentValue, q: false },
  ];

  return {
    kind: 'synthetic',
    points,
    annotation: {
      month: currentMonth,
      label: dayOneAnnotationLabel(input.asOf ?? now),
    },
    headroom: SYNTHETIC_HEADROOM,
  };
}
